import { IPromiseResolveGeneric } from 'library/ts/interfaces/IPromiseResolveGeneric';
import { ContentType } from 'library/ts/model/enums/content/ContentType';
import { PlayerAdapterEvent } from 'library/ts/player/adapter/events/PlayerAdapterEvent';
import { IPlayerAdapterErrorEventDetail } from 'library/ts/player/adapter/interfaces/events/detail/IPlayerAdapterErrorEventDetail';
import { IPlayerAdapterEventVO } from 'library/ts/player/adapter/interfaces/events/IPlayerAdapterEventVO';
import { IPlayerAdapter } from 'library/ts/player/adapter/IPlayerAdapter';
import { IPlayerAdapterAPI } from 'library/ts/player/adapter/IPlayerAdapterAPI';
import { ILogger } from 'library/ts/utilities/logger/interfaces/ILogger';
import { LogService } from 'library/ts/utilities/logger/LogService';
import { Utilities } from 'library/ts/utilities/Utilities';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { TagEventVO } from 'tag/ts/tag/events/TagEventVO';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { ITagEventVO } from 'tag/ts/tag/interfaces/events/ITagEventVO';
import { ITag } from 'tag/ts/tag/interfaces/ITag';
import { ITagPlayerManager } from 'tag/ts/tag/interfaces/player/ITagPlayerManager';
import { TagLoggerString } from 'tag/ts/tag/model/enums/TagLoggerString';
import { SkinMode } from 'tag/ts/tag/model/type/SkinMode';
import { PlayerAdapterType } from 'tag/ts/tag/player/enums/PlayerAdapterType';
import { IPlayerEventOrchestrator } from './interfaces/IPlayerEventOrchestrator';
import { IPlayerStateManager } from './interfaces/IPlayerStateManager';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description Orchestrates event coordination and player adapter integration
 *
 * **Event Orchestration Scope:**
 * - Player adapter lifecycle management and integration
 * - Event routing between player adapter and Smart Tag components
 * - Tag event handling and coordination
 * - Player adapter event processing and transformation
 * - Initialization and setup coordination
 * - Cross-component communication facilitation
 *
 * **Player Adapter Management:**
 * Handles the complete player adapter lifecycle:
 * - Instantiation of VTG Avia player adapter
 * - Dependency loading and initialization
 * - Player creation event coordination
 * - Error handling and recovery workflows
 * - Resource loading coordination
 *
 * **Event Processing Pipeline:**
 * Manages complex event flows between:
 * - Smart Tag application layer
 * - Player adapter layer
 * - VTG player layer
 * - Cross-component state synchronization
 *
 * **State Management Integration:**
 * Coordinates with PlayerStateManager for:
 * - Autoplay capability detection
 * - Time value synchronization
 * - State persistence across player operations
 *
 * **Extracted from PlayerManager:**
 * Consolidates event orchestration logic for better maintainability,
 * focused testing, and clear separation of concerns.
 */
export class PlayerEventOrchestrator implements IPlayerEventOrchestrator {
	#logger: ILogger;
	#tag: ITag;
	#utils: Utilities;
	#initialized: boolean = false;

	// Player adapter management
	#playerAdapter: IPlayerAdapter;

	// Constants
	private static readonly LOG_DESTRUCTION_PREFIX = 'onPlayerAdapterEvent:playerDestructionCallback:';
	private static readonly LOG_ERROR_EVENT_PREFIX = 'onPlayerAdapterEvent:handleErrorEventLogging:';
	private static readonly LOG_HANDLE_EVENT_PREFIX = 'onPlayerAdapterEvent:handleEventLogging:';

	constructor( tag: ITag, utils: Utilities ) {
		this.#tag = tag;
		this.#utils = utils;
		this.#logger = LogService.create( {
			prefixes: [ 'SmartTag:', 'Tag:', tag?.name || 'Unknown', 'PlayerEventOrchestrator:' ]
		} );
		this.#logger.log( 'PlayerEventOrchestrator created' );
	}

	/**
	 * @description Returns instance of player adapter class
	 */
	get playerAdapter(): IPlayerAdapter {
		return this.#playerAdapter;
	}

	/**
	 * @description Returns VTG player instance through adapter
	 */
	get player(): IPlayerAdapterAPI {
		return this.#playerAdapter?.player;
	}

	/**
	 * @description Initializes event orchestration and player adapter
	 *
	 * **Comprehensive Initialization Process:**
	 * - Sets up Tag event subscriptions for coordination
	 * - Creates VTG Avia player adapter instance
	 * - Loads player adapter dependencies
	 * - Coordinates window event handler setup
	 */
	initialize(): Promise<IPromiseResolveGeneric> {
		return new Promise(
			( resolve, reject ) => {
				// Set up Tag event subscriptions
				this.#tag.tagEventsHandler(
					this.onTagEvent.bind( this ),
					[
						TagEvent.ON_API_RESET_TAG_REQUEST,
						TagEvent.ON_CREATE_PLAYER_SUCCESS
					],
					undefined
				);

				// create player adapter class instance
				this.#playerAdapter = new ( window.SmartTag.playerAdapters[ PlayerAdapterType.AVIA ] )( { tag: this.#tag } as ITagAbstractClassArgument );

				// request player adapter to load its own dependencies
				this.#playerAdapter.initialize().then(
					( data ) => {
						this.#logger.log( 'initialize:', data );

						// Coordinate window event handler setup through lifecycle manager
						const lifecycleManager = this.#tag?.controller?.playerManager?.lifecycleManager;
						if ( lifecycleManager?.initialize ) {
							lifecycleManager.initialize().then( () => {
								this.#initialized = true;
								resolve( { msg: 'Done', success: true, result: this } );
							} ).catch( ( error ) => {
								this.#logger.warn( 'initialize: lifecycle manager setup failed:', error );
								// Continue without lifecycle manager if it fails
								this.#initialized = true;
								resolve( { msg: 'Done (lifecycle warning)', success: true, result: this } );
							} );
						} else {
							this.#initialized = true;
							resolve( { msg: 'Done', success: true, result: this } );
						}
					}
				).catch(
					( error ) => {
						this.#logger.error( 'initialize: Player adapter initialization failed:', error );
						reject( { msg: 'Player adapter initialization failed', error } );
					}
				);
			}
		);
	}

	/**
	 * @description Player Adapter Event handler
	 *
	 * **Complex Event Processing:**
	 * Handles sophisticated player adapter event processing including:
	 * - Fatal error detection and player destruction coordination
	 * - State synchronization for Chromecast functionality
	 * - Event filtering and selective logging
	 * - Cross-component event routing and transformation
	 */
	onPlayerAdapterEvent( tagEvent: ITagEventVO ): void {
		const playerAdapterEvent = tagEvent.data.playerAdapterEvent;
		const stateManager = this.#tag?.controller?.playerManager?.stateManager;

		/**
		 * @description callback to execute after player is destroyed
		 */
		const playerDestructionCallback = ( success: boolean, result: unknown ): void => {
			this.#logger.log( PlayerEventOrchestrator.LOG_DESTRUCTION_PREFIX, 'player removed:', success, result ? '[Result]' : 'undefined' );
		};

		/**
		 * @description Resets playback related properties set previously from avia for Chromecast
		 */
		const resetPlaybackProperties = (): void => {
			if ( stateManager?.resetTimeValues ) {
				stateManager.resetTimeValues();
			}
		};

		// setup event filtering for logging
		this.#handleEventLogging( playerAdapterEvent, playerDestructionCallback );

		// store content time and stream time locally for Chromecast sender application
		this.#updatePlaybackTimeValues( playerAdapterEvent, stateManager );

		// filter per event type - EXACT match with 1.37.2 PlayerManager
		switch ( playerAdapterEvent.type ) {
			case PlayerAdapterEvent.RESOURCE_START:
				resetPlaybackProperties();
				break;
			case PlayerAdapterEvent.AD_BREAK_START:
			case PlayerAdapterEvent.AD_START:
			case PlayerAdapterEvent.AD_PROGRESS:
				this.#setContentType( ContentType.AD, stateManager );
				break;
			case PlayerAdapterEvent.CONTENT_START:
			case PlayerAdapterEvent.CONTENT_SEGMENT_START:
				this.#setContentType( ContentType.CONTENT, stateManager );
				break;
			case PlayerAdapterEvent.CONTENT_PROGRESS:
				this.#setPlaybackProperties( stateManager );
				this.#setContentType( ContentType.CONTENT, stateManager );
				break;
		}

		// IMPORTANT: re-dispatch player event across Tag as internal event
		this.#tag.dispatchTagEvent( tagEvent );
	}

	/**
	 * @description Handles video player creation success event
	 *
	 * **Post-Creation Coordination:**
	 * Manages complex post-player-creation logic including:
	 * - Autoplay capability detection coordination
	 * - Player type-specific resource loading
	 * - Skin integration scenarios
	 * - Resource loading request dispatching
	 */
	onCreateVideoPlayerSuccess(): void {
		this.#logger.log( 'onCreateVideoPlayerSuccess' );

		// done processing video player create success => proceed further
		const doneProcessing = (): void => {
			this.#logger.log( 'onCreateVideoPlayerSuccess', 'player type:', this.#tag?.view?.playerType );
			this.#handlePlayerTypeResourceLoading();
		};

		// check if autoplay was already detected internally
		this.#handleAutoplayDetection( doneProcessing );
	}

	/**
	 * @description Tag application event handler
	 *
	 * **Event Coordination:**
	 * Processes Tag-level events and coordinates appropriate responses
	 * across the player management system.
	 */
	onTagEvent( event: TagEventVO ): void {
		// filter by event type
		switch ( event?.type ) {
			case TagEvent.ON_CREATE_PLAYER_SUCCESS:
				this.#logger.log( TagLoggerString.ON_TAG_EVENT, event?.type, event?.data );
				this.onCreateVideoPlayerSuccess();
				break;
			case TagEvent.ON_API_RESET_TAG_REQUEST:
				this.#logger.log( TagLoggerString.ON_TAG_EVENT, TagEvent.ON_API_RESET_TAG_REQUEST );
				const lifecycleManager = this.#tag?.controller?.playerManager?.lifecycleManager;
				if ( lifecycleManager?.executeTagReset ) {
					lifecycleManager.executeTagReset( event ); // request tag reset
				}
				break;
		}
	}

	/**
	 * @description Returns whether event orchestrator is properly initialized
	 */
	isInitialized(): boolean {
		return this.#initialized;
	}

	/**
	 * @description Resets event orchestrator to initial state
	 */
	reset(): void {
		this.#playerAdapter = undefined;
		this.#initialized = false;
		this.#logger.log( 'reset: Event orchestrator reset to initial state' );
	}

	/**
	 * @description Handles event logging based on event type
	 * @param {IPlayerAdapterEventVO} playerAdapterEvent
	 * @param {(success: boolean, result: unknown) => void} playerDestructionCallback
	 */
	#handleEventLogging( playerAdapterEvent: IPlayerAdapterEventVO, playerDestructionCallback: ( success: boolean, result: unknown ) => void ): void {
		switch ( playerAdapterEvent.type ) {
			case PlayerAdapterEvent.ERROR:
				this.#handleErrorEventLogging( playerAdapterEvent, playerDestructionCallback );
				break;
			case PlayerAdapterEvent.ACTIVE_STATE_CHANGE:
			case PlayerAdapterEvent.AD_PROGRESS:
			case PlayerAdapterEvent.CLOCK:
			case PlayerAdapterEvent.CONTENT_PROGRESS:
			case PlayerAdapterEvent.LOG_EVENT:
			case PlayerAdapterEvent.METRIC:
				// do not log
				break;
			default:
				this.#logger.log( PlayerEventOrchestrator.LOG_HANDLE_EVENT_PREFIX, playerAdapterEvent );
		}
	}

	/**
	 * @description Handles error event logging and player destruction
	 * @param {IPlayerAdapterEventVO} playerAdapterEvent
	 * @param {(success: boolean, result: unknown) => void} playerDestructionCallback
	 */
	#handleErrorEventLogging( playerAdapterEvent: IPlayerAdapterEventVO, playerDestructionCallback: ( success: boolean, result: unknown ) => void ): void {
		// if the event detail is not empty
		if ( !this.#utils.Type.isEmpty( playerAdapterEvent?.detail ) ) {
			const errorDetail = ( playerAdapterEvent?.detail as IPlayerAdapterErrorEventDetail )?.error;
			// and error is fatal
			if ( errorDetail?.fatal === true ) {
				this.#logger.log( PlayerEventOrchestrator.LOG_ERROR_EVENT_PREFIX, playerAdapterEvent );

				// a network fatal while already known-offline doesn't end the session — skip the
				// destroy so the player instance can recover once the network returns. Opt-in only
				// (PLBCK-8332): returns false unless the host set TAG.offlineFatalRecovery === true
				if ( this.#tag?.tagUtilities?.isNetworkFatalWhileOffline( errorDetail ) ) {
					this.#logger.log( PlayerEventOrchestrator.LOG_ERROR_EVENT_PREFIX, 'skipping destroy: network fatal while offline', errorDetail );
					return;
				}

				// request player removal / destruction
				const lifecycleManager = this.#tag?.controller?.playerManager?.lifecycleManager;
				if ( lifecycleManager?.requestPlayerDestruction ) {
					lifecycleManager.requestPlayerDestruction( playerDestructionCallback );
				}
			}
		}
	}

	/**
	 * @description Sets content type based on player adapter events
	 * @param {ContentType} contentType
	 * @param {IPlayerStateManager} stateManager
	 */
	#setContentType( contentType: ContentType, stateManager: IPlayerStateManager ): void {
		if ( stateManager?.setContentType ) {
			stateManager.setContentType( contentType );
		}
	}

	/**
	 * @description Pulls playback related properties from avia player for Chromecast
	 * @param {IPlayerStateManager} stateManager
	 */
	#setPlaybackProperties( stateManager: IPlayerStateManager ): void {
		if ( stateManager ) {
			stateManager.setContentTime( this.player?.contentTime );
			stateManager.setStreamTime( this.player?.streamTime );
			stateManager.setPlaybackTime( this.player?.playbackTime );
			stateManager.setLiveStreamUtcStart( this.player?.liveStreamUtcStart );
			stateManager.setLiveStreamUtcTime( this.player?.liveStreamUtcTime );
			stateManager.setLiveStreamUtcDuration( this.player?.liveStreamUtcDuration );
		}
	}

	/**
	 * @description Updates playback time values for cross-component persistence
	 *
	 * **Chromecast Integration:**
	 * Persists content and stream time values that are critical for Chromecast sender
	 * application functionality. These values must be retained even when VTG player
	 * resource is killed during Cast Start events.
	 *
	 * **Time Value Persistence:**
	 * - Updates on ALL events except RESOURCE_END
	 * - Validates values are valid numbers > 0
	 * - Stores in PlayerStateManager for cross-component access
	 *
	 * @param {IPlayerAdapterEventVO} playerAdapterEvent - Current player event
	 * @param {IPlayerStateManager} stateManager - State manager for time storage
	 */
	#updatePlaybackTimeValues( playerAdapterEvent: IPlayerAdapterEventVO, stateManager: IPlayerStateManager ): void {
		// store content time and stream time locally, prevent resetting these values on VTG player events that reset them
		// content time + stream time values are used by Chromecast sender application and must be retained
		// after VTG player resource is killed (i.e. in skin PlayerWrapper on Cast Start event)
		if ( playerAdapterEvent.type !== PlayerAdapterEvent.RESOURCE_END ) {
			const contentTime: number = this.player?.contentTime;
			const streamTime: number = this.player?.streamTime;
			if ( this.#utils.Type.isNumber( contentTime ) && contentTime > 0 ) {
				stateManager?.setContentTime( contentTime );
			}
			if ( this.#utils.Type.isNumber( streamTime ) && streamTime > 0 ) {
				stateManager?.setStreamTime( streamTime );
			}
		}
	}

	/**
	 * @description Handles resource loading based on player type
	 */
	#handlePlayerTypeResourceLoading(): void {
		const playerManager = this.#tag?.controller?.playerManager;
		switch ( this.#tag?.model?.skinMode ) {
			case SkinMode.AVIA_UI: // avia-js-ui
			case SkinMode.CUSTOM_MOBILE: // custom Mobile Skin (src/skin_mobile/) — no real Skin-driven
				// loadTagResource() decision logic yet, so Tag auto-loads same as AVIA_UI
				this.#loadResourceForPlayer( playerManager, 'FULL' );
				break;
			case SkinMode.NONE: // no Smart Tag skin at all (e.g. CTV host with its own UI)
				this.#loadResourceForPlayer( playerManager, 'CHROMELESS' );
				break;
		}
	}

	/**
	 * @description Loads resource for specific player type
	 * @param {ITagPlayerManager} playerManager
	 * @param {string} playerType
	 */
	#loadResourceForPlayer( playerManager: ITagPlayerManager, playerType: string ): void {
		if ( playerManager?.loadDefaultResource ) {
			playerManager.loadDefaultResource().then(
				( result ) => {
					this.#logger.log( `PlayerEventOrchestrator: Resource loaded successfully for ${playerType} player`, result );
				}
			).catch(
				( error ) => {
					this.#logger.warn( `PlayerEventOrchestrator: Resource loading failed for ${playerType} player`, error );
				}
			);
		}
	}

	/**
	 * @description Handles autoplay detection logic
	 * @param {() => void} doneProcessing
	 */
	#handleAutoplayDetection( doneProcessing: () => void ): void {
		const shouldDetectAutoplay =
			this.#utils.Type.isEmpty( window.SmartTag.autoplay )
			&&
			window.SmartTag.runtimeVO?.readyCallback?.detectAutoplay === true;

		switch ( shouldDetectAutoplay ) {
			case false: // detected internally
				doneProcessing();
				break;
			case true: // autoplay was not detected internally
				const stateManager = this.#tag?.controller?.playerManager?.stateManager;
				if ( !stateManager?.autoplay ) {
					// get autoplay capabilities from player adapter
					this.#playerAdapter.getAutoplayCapabilities().then(
						( data ) => {
							if ( stateManager?.setAutoplay ) {
								stateManager.setAutoplay( data.autoplayInfo );
							}
							doneProcessing();
						}
					);
				} else {
					doneProcessing();
				}
				break;
		}
	}
}